import React, { useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import confetti from 'canvas-confetti';
import './CelebrationOverlay.css';

const CelebrationOverlay = ({ onComplete }) => {
  const [show, setShow] = useState(true);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    // Handle window resize
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);

    // Fast Fireworks sequence
    const fireworksSequence = () => {
      // Firework 1 - Red burst from left
      setTimeout(() => {
        confetti({
          particleCount: 150,
          spread: 80,
          origin: { x: 0.2, y: 0.5 },
          colors: ['#ff0000', '#ff6b6b', '#ffd700', '#ffffff'],
          startVelocity: 70,
          gravity: 1,
          scalar: 1.2,
        });
      }, 300);

      // Firework 2 - Blue burst from right
      setTimeout(() => {
        confetti({
          particleCount: 150,
          spread: 80,
          origin: { x: 0.8, y: 0.5 },
          colors: ['#0099ff', '#54a0ff', '#ffd700', '#ffffff'],
          startVelocity: 70,
          gravity: 1,
          scalar: 1.2,
        });
      }, 600);

      // Firework 3 - Gold burst from center
      setTimeout(() => {
        confetti({
          particleCount: 200,
          spread: 120,
          origin: { x: 0.5, y: 0.4 },
          colors: ['#ffd700', '#ffed4e', '#ff6b6b', '#ffffff'],
          startVelocity: 80,
          gravity: 1,
          scalar: 1.3,
        });
      }, 900);

      // Firework 4 - Green burst
      setTimeout(() => {
        confetti({
          particleCount: 150,
          spread: 90,
          origin: { x: 0.3, y: 0.3 },
          colors: ['#00ff00', '#4ecdc4', '#ffd700', '#ffffff'],
          startVelocity: 75,
          gravity: 1,
          scalar: 1.2,
        });
      }, 1200);

      // Firework 5 - Purple burst
      setTimeout(() => {
        confetti({
          particleCount: 150,
          spread: 90,
          origin: { x: 0.7, y: 0.3 },
          colors: ['#9b59b6', '#ff6b6b', '#ffd700', '#ffffff'],
          startVelocity: 75,
          gravity: 1,
          scalar: 1.2,
        });
      }, 1500);

      // Firework 6 - Multi-color burst center
      setTimeout(() => {
        confetti({
          particleCount: 250,
          spread: 140,
          origin: { x: 0.5, y: 0.2 },
          colors: ['#ff0000', '#00ff00', '#0099ff', '#ffd700', '#ff6b6b'],
          startVelocity: 90,
          gravity: 1,
          scalar: 1.4,
        });
      }, 1800);

      // Stars burst
      setTimeout(() => {
        confetti({
          particleCount: 120,
          spread: 180,
          origin: { x: 0.5, y: 0.15 },
          colors: ['#ffd700', '#ffffff'],
          shapes: ['star'],
          startVelocity: 60,
          gravity: 1.2,
          scalar: 1.5,
        });
      }, 2100);

      // Side cannons - Grand finale
      setTimeout(() => {
        const duration = 2.5 * 1000;
        const end = Date.now() + duration;

        const frame = () => {
          confetti({
            particleCount: 10,
            angle: 60,
            spread: 60,
            origin: { x: 0, y: 0.7 },
            colors: ['#ff0000', '#ffd700', '#00ff00', '#0099ff'],
            startVelocity: 50,
            scalar: 1.2,
          });
          confetti({
            particleCount: 10,
            angle: 120,
            spread: 60,
            origin: { x: 1, y: 0.7 },
            colors: ['#ff0000', '#ffd700', '#00ff00', '#0099ff'],
            startVelocity: 50,
            scalar: 1.2,
          });

          if (Date.now() < end) {
            requestAnimationFrame(frame);
          }
        };

        frame();
      }, 2500);
    };

    fireworksSequence();

    // Auto-hide after 6 seconds (faster)
    const timer = setTimeout(() => {
      setShow(false);
      if (onComplete) onComplete();
    }, 6000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [onComplete]);

  if (!show) return null;

  return (
    <div className="celebration-overlay-new">
      {/* React Confetti Background */}
      <Confetti
        width={windowSize.width}
        height={windowSize.height}
        numberOfPieces={600}
        recycle={true}
        gravity={0.4}
        wind={0.01}
        colors={['#ff0000', '#ffd700', '#00ff00', '#0099ff', '#ff6b6b', '#4ecdc4', '#9b59b6']}
      />

      {/* Animated Rockets */}
      <div className="rocket-container">
        <div className="rocket rocket-anim-1">
          <div className="rocket-body-new">🚀</div>
          <div className="rocket-trail-new"></div>
        </div>

        <div className="rocket rocket-anim-2">
          <div className="rocket-body-new">🚀</div>
          <div className="rocket-trail-new"></div>
        </div>

        <div className="rocket rocket-anim-3">
          <div className="rocket-body-new">🚀</div>
          <div className="rocket-trail-new"></div>
        </div>

        <div className="rocket rocket-anim-4">
          <div className="rocket-body-new">🚀</div>
          <div className="rocket-trail-new"></div>
        </div>

        <div className="rocket rocket-anim-5">
          <div className="rocket-body-new">🚀</div>
          <div className="rocket-trail-new"></div>
        </div>

        <div className="rocket rocket-anim-6">
          <div className="rocket-body-new">🚀</div>
          <div className="rocket-trail-new"></div>
        </div>
      </div>

      {/* Gift Boxes Popping */}
      <div className="gifts-container">
        <div className="gift-pop gift-pop-1">
          <div className="gift-emoji">🎁</div>
          <div className="gift-sparkles">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="gift-sparkle" style={{ '--angle': `${(360 / 12) * i}deg` }}>
                ✨
              </div>
            ))}
          </div>
        </div>

        <div className="gift-pop gift-pop-2">
          <div className="gift-emoji">🎁</div>
          <div className="gift-sparkles">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="gift-sparkle" style={{ '--angle': `${(360 / 12) * i}deg` }}>
                ✨
              </div>
            ))}
          </div>
        </div>

        <div className="gift-pop gift-pop-3">
          <div className="gift-emoji">🎁</div>
          <div className="gift-sparkles">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="gift-sparkle" style={{ '--angle': `${(360 / 12) * i}deg` }}>
                ✨
              </div>
            ))}
          </div>
        </div>

        <div className="gift-pop gift-pop-4">
          <div className="gift-emoji">🎁</div>
          <div className="gift-sparkles">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="gift-sparkle" style={{ '--angle': `${(360 / 12) * i}deg` }}>
                ✨
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating Balloons */}
      <div className="balloons-container">
        <div className="balloon balloon-1">🎈</div>
        <div className="balloon balloon-2">🎈</div>
        <div className="balloon balloon-3">🎈</div>
        <div className="balloon balloon-4">🎈</div>
        <div className="balloon balloon-5">🎈</div>
        <div className="balloon balloon-6">🎈</div>
        <div className="balloon balloon-7">🎈</div>
      </div>

      {/* Sparklers */}
      <div className="sparkler-bottom-left">
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className="sparkler-dot"
            style={{
              '--delay': `${Math.random() * 1.5}s`,
              '--angle': `${-90 + (Math.random() * 50 - 25)}deg`,
            }}
          />
        ))}
      </div>

      <div className="sparkler-bottom-right">
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className="sparkler-dot"
            style={{
              '--delay': `${Math.random() * 1.5}s`,
              '--angle': `${-90 + (Math.random() * 50 - 25)}deg`,
            }}
          />
        ))}
      </div>

      {/* Firework SVG Animations */}
      <svg className="firework-svg" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="firework-gradient-1">
            <stop offset="0%" stopColor="#ff0000" stopOpacity="1" />
            <stop offset="100%" stopColor="#ffd700" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="firework-gradient-2">
            <stop offset="0%" stopColor="#0099ff" stopOpacity="1" />
            <stop offset="100%" stopColor="#00ff00" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="firework-gradient-3">
            <stop offset="0%" stopColor="#ffd700" stopOpacity="1" />
            <stop offset="100%" stopColor="#ff6b6b" stopOpacity="0" />
          </radialGradient>
        </defs>
        
        <circle className="firework-circle firework-circle-1" r="0" cx="20%" cy="25%" fill="url(#firework-gradient-1)" />
        <circle className="firework-circle firework-circle-2" r="0" cx="80%" cy="30%" fill="url(#firework-gradient-2)" />
        <circle className="firework-circle firework-circle-3" r="0" cx="50%" cy="20%" fill="url(#firework-gradient-3)" />
        <circle className="firework-circle firework-circle-4" r="0" cx="35%" cy="40%" fill="url(#firework-gradient-1)" />
        <circle className="firework-circle firework-circle-5" r="0" cx="65%" cy="35%" fill="url(#firework-gradient-2)" />
        <circle className="firework-circle firework-circle-6" r="0" cx="50%" cy="50%" fill="url(#firework-gradient-3)" />
      </svg>
    </div>
  );
};

export default CelebrationOverlay;